import { forwardRef, ReactNode, useEffect, useState } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { TextInputAlign } from '~/components/ui/TextInput/TextInput.model';
import {
	StyledContainer,
	StyledIconSlot,
	StyledInput,
	StyledLabel,
	StyledUnit,
} from './MoneyInput.styled';

interface IMoneyInputProps {
	value: number;
	onChange: (value: number) => void;
	label?: string;
	icon?: ReactNode;
	placeholder?: string;
	height?: number;
	fontSize?: number;
	padding?: string;
	textAlign?: TextInputAlign;
	placeholderOpacity?: number;
	disabled?: boolean;
	error?: boolean;
	min?: number;
	max?: number;
	unit?: string;
}

const MoneyInput = forwardRef<HTMLInputElement, IMoneyInputProps>(
	(
		{
			value,
			onChange,
			label,
			icon,
			placeholder,
			height = 40,
			fontSize = 14,
			padding = '0 15px',
			textAlign = TextInputAlign.Right,
			placeholderOpacity = 0.25,
			disabled,
			error,
			min,
			max,
			unit,
		},
		ref,
	) => {
		const [inputValue, setInputValue] = useState<number>(value || 0);

		useEffect(() => {
			setInputValue(value ?? 0);
		}, [value]);

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			e.preventDefault();
			const raw = e.target.value.replace(/\D/g, '');

			if (raw.length === 0) {
				setInputValue(0);
				onChange(0);
				return;
			}

			let parsed = parseInt(raw, 10);
			if (typeof max === 'number' && parsed > max) parsed = max;
			if (typeof min === 'number' && parsed < min) parsed = min;

			setInputValue(parsed);
			onChange(parsed);
		};

		return (
			<StyledContainer
				$height={height}
				$padding={padding}
				$error={error}
				$disabled={disabled}
			>
				{label && <StyledLabel $fontSize={fontSize}>{label}</StyledLabel>}
				<StyledInput
					ref={ref}
					tabIndex={-1}
					value={inputValue ? inputValue.toLocaleString('ru-RU') : ''}
					onChange={handleChange}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
					placeholder={placeholder}
					disabled={disabled}
					$fontSize={fontSize}
					$textAlign={textAlign}
					$placeholderOpacity={placeholderOpacity}
				/>
				{icon && <StyledIconSlot>{icon}</StyledIconSlot>}
				{unit && <StyledUnit>{unit}</StyledUnit>}
			</StyledContainer>
		);
	},
);

export { MoneyInput };
